import { useEffect, useState, useMemo, useCallback } from "react";
import "../../molecules/optionLayouts/Scq.css?__remix_sideEffect__";
import {
  Radio,
  RadioGroup,
  Box,
  Text,
  Image,
  HStack,
  Wrap,
  Flex } from
"@chakra-ui/react";
import { useRoot } from "../../../context/RootContext";
import { renderContent } from "../../../utils/TutorConstants";
import ImageModal from "../../atoms/ImageModal";
import {
  extractGivenAnswers,
  hasLongString,
  isNotEmptyOrNull } from
"../../../utils/common-utils";
import useStateRef from "react-usestateref";
import { taskTypes } from "../../../utils/LearningPathUtils";
import { getPresentationModeBasedOnTask } from "../../../utils/presentationModes";
import { useTutor } from "../../../context/AiTutorContext";
import CheckmarkCircle from "../../../assets/svgs/checkmarkCircle.svg";
import closeCircle from "../../../assets/svgs/closeCircle.svg";
import {
  answerEncryptText,
  answers_index_hashed_keys } from
"../../../utils/crypto";

export const Scq = (props) => {
  const [value, setValue] = useState(
    isNotEmptyOrNull(props.userAnswer) ?
    extractGivenAnswers(props.userAnswer)?.[0] :
    ""
  );
  const [selectedIndex, setSelectedIndex] = useState();
  const { isStandaloneWithSubjectMapping, isStandalone, isReviewScreen } =
  useRoot();

  //missed item
  const [optionsData, setOptionsData] = useState(props.options);
  const [isOpen, setIsOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState();
  const [onAnswerSubmitted, setOnAnswerSubmitted, onAnswerSubmittedRef] =
  useStateRef(
    props.onAnswerSubmitted ?
    props.onAnswerSubmitted :
    isNotEmptyOrNull(props.userAnswer) ?
    true :
    false
  );
  const { isFactual } = useTutor();
  const [hashedOptions, setHashedOptions] = useState({});
  const [isHashingComplete, setIsHashingComplete] = useState(false);

  const [presentationMode, setPresentationMode] = useState(
    getPresentationModeBasedOnTask(
      props?.currentQuestion?.factual === true ?
      taskTypes.factualMode :
      isReviewScreen ?
      taskTypes.reviewMode :
      isFactual ?
      taskTypes.assessment :
      isStandalone || isStandaloneWithSubjectMapping ?
      taskTypes.standalone :
      props.taskType
    )
  );

  // Memoize hashed options to prevent multiple async calls
  useEffect(() => {
    const computeHashedOptions = async () => {
      if (!optionsData.length) return;

      const hashed = {};
      for (let i = 0; i < optionsData.length; i++) {
        const item = optionsData[i];
        let data = await (isNotEmptyOrNull(
          props?.currentQuestion?.answers_index_hashed_keys
        ) ?
        answers_index_hashed_keys(
          props?.currentQuestion?.uuid + window.ENV.HASH_SECRET_KEY + i
        ) :
        isNotEmptyOrNull(props?.currentQuestion?.answers_key) ?
        answerEncryptText(props?.currentQuestion?.options_layout?.[i]) :
        item);

        hashed[i] = data;
      }
      setHashedOptions(hashed);
      setIsHashingComplete(true);
    };

    computeHashedOptions();
  }, [
  optionsData,
  props?.currentQuestion?.uuid,
  props?.currentQuestion?.answers_index_hashed_keys,
  props?.currentQuestion?.answers_key]
  );

  // Optimized function that uses memoized values
  const getHashedOption = useCallback(
    (item, position) => {
      if (!isHashingComplete) return item; // Fallback while hashing
      return hashedOptions[position] || item;
    },
    [hashedOptions, isHashingComplete]
  );

  const handleOnChange = async (item, index) => {
    setValue(item);
    setSelectedIndex(index);

    // Use the memoized hashed option
    let selectedAnswer = getHashedOption(item, index);

    props.handleAnswerChange({
      selectedItem: item,
      selectedHashedIndexAnswer: selectedAnswer,
      selectedIndex: index
    });
  };

  useEffect(() => {
    if (props.onAnswerSubmitted) {
      let selectedAnswer = getHashedOption(value, selectedIndex);
      setValue(selectedAnswer);
      setOnAnswerSubmitted(props.onAnswerSubmitted);
    }
  }, [props.onAnswerSubmitted]);

  const getAnswerStyle = useCallback(
    (item, value, answerSubmitted, imagesList, currentQuestion, position) => {
      const hashedItem = getHashedOption(item, position);
      const hashedValue = value ?
      getHashedOption(value, optionsData.indexOf(value)) :
      value;

      const index = props?.displayedQues?.findIndex(
        (item) => item.version_uuid === currentQuestion?.version_uuid
      );

      if (
      currentQuestion?.factual === true ?
      props?.displayedQues?.[index].isSubmitted !== true :
      !answerSubmitted)
      {
        if (position === selectedIndex) {
          return imagesList?.length > 0 ?
          "scq-card-selected" :
          "scq-card-selected-noimage";
        } else {
          return imagesList?.length > 0 ? "scq-card" : "scq-card-noimage";
        }
      } else {
        if (hashedItem === props?.answer?.[0]) {
          if (isReviewScreen) {
            return imagesList?.length > 0 ?
            "scq-card-correct" :
            "scq-card-correct-noimage";
          } else if (
          presentationMode.DisplayCorrectAnswerInstantly ||
          presentationMode.DisplayCorrectAnswerAfterNextClick &&
          currentQuestion?.showGreen)
          {
            return imagesList?.length > 0 ?
            "scq-card-correct" :
            "scq-card-correct-noimage";
          } else {
            if (hashedItem === hashedValue) {
              return imagesList?.length > 0 ?
              "scq-card-selected" :
              "scq-card-selected-noimage";
            } else {
              return imagesList?.length > 0 ? "scq-card" : "scq-card-noimage";
            }
          }
        } else if (hashedItem === hashedValue) {
          if (isReviewScreen) {
            return imagesList?.length > 0 ?
            "scq-card-wrong" :
            "scq-card-wrong-noimage";
          } else if (
          presentationMode.DisplayWrongAnswerInstantly ||
          presentationMode.DisplayWrongAnswerAfterNextClick &&
          currentQuestion.showRed)
          {
            return imagesList?.length > 0 ?
            "scq-card-wrong" :
            "scq-card-wrong-noimage";
          } else {
            return props.taskType === taskTypes.assessment || isFactual ?
            imagesList?.length > 0 ?
            "scq-card-selected" :
            "scq-card-selected-noimage" :
            imagesList?.length > 0 ?
            "scq-card-selected" :
            "scq-card-selected-noimage";
          }
        } else {
          return imagesList?.length > 0 ? "scq-card" : "scq-card-noimage";
        }
      }
    },
    [
    getHashedOption,
    optionsData,
    selectedIndex,
    isReviewScreen,
    presentationMode,
    props?.answer,
    props?.taskType,
    isFactual,
    props?.displayedQues]

  );

  const optionStyles = useMemo(() => {
    if (!isHashingComplete) return {};

    const styles = {};
    optionsData.forEach((item, index) => {
      const style = getAnswerStyle(
        item,
        value,
        onAnswerSubmittedRef.current,
        props?.option_image_details,
        props?.currentQuestion,
        index
      );

      styles[index] = {
        style,
        correctAnswerIndicator:
        style === "scq-card-correct" || style === "scq-card-correct-noimage",
        wrongAnswerIndicator:
        style === "scq-card-wrong" || style === "scq-card-wrong-noimage",
        selectedIndicator:
        style === "scq-card-selected" ||
        style === "scq-card-selected-noimage"
      };
    });

    return styles;
  }, [
  optionsData,
  value,
  onAnswerSubmittedRef.current,
  props?.option_image_details,
  props?.currentQuestion,
  getAnswerStyle,
  isHashingComplete]
  );

  const openImage = (index) => {
    setImageIndex(index);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isHashingComplete) {
    return <div>Loading options...</div>;
  }

  return (
    <div
      id="scq-wrapper"
      style={props?.isDontAllow ? { pointerEvents: "none" } : {}}>

      <Flex
        id="scq-options-container"
        flexDirection={[
        "column",
        hasLongString(optionsData) ? "column" : "row",
        hasLongString(optionsData) ? "column" : "row"]
        }
        gap={"14px"}>

        {optionsData.map((item, index) => {
          const optionStyle = optionStyles[index] || {};
          const { style, correctAnswerIndicator, wrongAnswerIndicator, selectedIndicator } =
          optionStyle;
          const optionLetter = String.fromCharCode(65 + index);
          return (
            <div key={`scq-option-${index}`} id={`scq-option-${index}`}>
              <Box
                className={style}
                variant={"outlined"}
                cursor={"pointer"}
                onClick={() => {
                  !onAnswerSubmittedRef.current && handleOnChange(item, index);
                }}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"8px"}>

                <div
                  id={`scq-option-content-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center"
                  }}>

                  <Box
                    id={`scq-option-label-${index}`}
                    borderRadius={"50%"}
                    bgColor={"white"}
                    padding={"8px"}
                    borderColor={"#8f82d7"}
                    borderWidth={"1px"}
                    display={"flex"}
                    h={"28px"}
                    justifyContent={"center"}
                    alignItems={"center"}>

                    <Text as="b" fontSize="md">
                      {optionLetter}
                    </Text>
                  </Box>
                  <div
                    id={`scq-option-text-wrapper-${index}`}
                    className={
                    hasLongString(optionsData) ?
                    "scq-content-moretext" :
                    "scq-content"
                    }>

                    <Text
                      id={`scq-option-text-${index}`}
                      className={"scq-text"}
                      variant="body2"
                      color={
                      correctAnswerIndicator || wrongAnswerIndicator ?
                      "#130159" :
                      selectedIndicator ?
                      "white" :
                      "#130159"
                      }>

                      {renderContent(props?.modifiedOptions?.[index])}
                    </Text>
                    {props.option_image_details?.length > 0 &&
                    <Box
                      id={`scq-option-image-container-${index}`}
                      padding={"10px"}>

                        <Image
                        id={`scq-option-image-${index}`}
                        className="scq-image"
                        onClick={() => {
                          openImage(index);
                        }}
                        src={props.option_image_details?.[index]?.url}
                        alt={`Image ${index}`} />

                      </Box>
                    }
                  </div>
                </div>

                <Box id={`scq-indicator-container-${index}`} display={"flex"}>
                  <Image
                    id={`scq-indicator-icon-${index}`}
                    visibility={
                    correctAnswerIndicator || wrongAnswerIndicator ?
                    "visible" :
                    "hidden"
                    }
                    src={correctAnswerIndicator ? CheckmarkCircle : closeCircle}
                    width={"24px"}
                    height={"24px"} />

                </Box>
              </Box>
            </div>);

        })}
      </Flex>
      {isOpen &&
      <div id="scq-image-modal-wrapper" style={{ background: "blue" }}>
          <ImageModal
          key={Math.random()}
          isOpen={isOpen}
          handleClose={handleClose}
          url={props.option_image_details?.[imageIndex]?.url} />

        </div>
      }
    </div>);

};